import {
  formatOrdinal,
  formatStationTime,
  resolveStationTimeZone,
  stationDayDiff,
} from './time'

export interface RelativeTimeLabel {
  label: string
  tooltip?: string
}

export const formatRelativeTime = (
  epochMs: number | null | undefined,
  stationTz?: string | null,
  nowEpochMs: number = Date.now(),
): RelativeTimeLabel => {
  if (!Number.isFinite(epochMs ?? Number.NaN)) {
    return { label: '—' }
  }

  const timestamp = Math.trunc(Number(epochMs))
  const timeZone = resolveStationTimeZone(stationTz)
  const tooltip = formatStationTime(timestamp, timeZone, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

  if (timestamp > nowEpochMs) {
    return { label: 'Just now', tooltip }
  }

  const diffMs = nowEpochMs - timestamp
  const diffMinutes = Math.floor(diffMs / 60000)

  if (diffMinutes < 60) {
    if (diffMinutes <= 1) {
      return { label: '·1 minute ago', tooltip }
    }
    return { label: `·${diffMinutes} minutes ago`, tooltip }
  }

  const dayDifference = stationDayDiff(nowEpochMs, timestamp, timeZone)

  if (dayDifference === 0) {
    const clock = formatStationTime(timestamp, timeZone, { hour: 'numeric', minute: '2-digit' })
    return { label: `Today @ ${clock}`, tooltip }
  }

  if (dayDifference === 1) {
    return { label: 'Yesterday', tooltip }
  }

  if (dayDifference > 1 && dayDifference < 7) {
    return { label: `·${dayDifference} days ago`, tooltip }
  }

  const dayValue = Number(formatStationTime(timestamp, timeZone, { day: 'numeric' }))
  const monthLabel = formatStationTime(timestamp, timeZone, { month: 'short' })
  const yearValue = Number(formatStationTime(timestamp, timeZone, { year: 'numeric' }))
  const nowYear = Number(formatStationTime(nowEpochMs, timeZone, { year: 'numeric' }))
  const ordinal = Number.isNaN(dayValue) ? '' : formatOrdinal(dayValue)

  if (!Number.isNaN(yearValue) && yearValue === nowYear) {
    return { label: `·${monthLabel} ${ordinal}`.trim(), tooltip }
  }

  const suffix = Number.isNaN(yearValue) ? '' : `, ${yearValue}`
  return { label: `·${monthLabel} ${ordinal}${suffix}`.trim(), tooltip }
}
